/**
 * Contract Selection review surface (DEV-735 / DEV-736).
 *
 * Renders the type-led contract recommendation stored on the step
 * (`review_context.report`). The list is usable as-is: it already applies every
 * fact on record about the program. Above it, a lightweight one-at-a-time
 * survey (`report.survey`) sharpens it — each answer is recorded through the
 * step interview's record-answer, the report is recomputed in place
 * (`POST /steps/:id/refresh/`), and the next question comes from the new
 * report. That makes it a search: an answer that rules a type out drops every
 * question that only led to it.
 *
 * One contract approach per program. Clicking a type (or a vehicle under it)
 * picks it: the pick slides to the top, outlined green, and clicking another
 * swaps them. Vehicle-based types (IDIQ, Schedule) can only be picked through
 * one of their vehicles; the type stays in the list with its other vehicles.
 * Picks save through `POST /steps/:id/selection/`.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react'
import { Button, Card, Input, Modal } from '../../components/ui'
import { api } from '../../services/api'
import {
  workflowsApi,
  type ContractCondition,
  type ContractReport,
  type ContractSelectionPick,
  type ContractSurveyQuestion,
  type ContractTypeItem,
  type ContractVehicleItem,
  type WorkflowStepDetail,
} from '../../services/workflows'

// Status tints via color-mix: the tokens carry no alpha channel, so Tailwind's
// `bg-x/10` shorthand renders transparent.
const tint = (token: string, pct = 12) => ({
  background: `color-mix(in oklch, var(--${token}) ${pct}%, transparent)`,
  color: `var(--${token})`,
})

function Pill({ style, children, title }: { style?: React.CSSProperties; children: React.ReactNode; title?: string }) {
  return (
    <span title={title} style={style}
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      {children}
    </span>
  )
}

function samePick(a: ContractSelectionPick | null, b: ContractSelectionPick | null) {
  if (!a || !b) return a === b
  return a.contract_type_id === b.contract_type_id && (a.vehicle_id ?? null) === (b.vehicle_id ?? null)
}

/**
 * FLIP: remember where each keyed element was just before a change, then after
 * React lays out the new order, start each one at its old spot and let it slide
 * to its new one. An element may name a `flipFrom` key to start where a
 * different element was (the pinned card starts at the vehicle row it came
 * from). Capture-only elements are measured but not animated themselves.
 */
function useFlip() {
  const animated = useRef(new Map<string, HTMLElement>())
  const measured = useRef(new Map<string, HTMLElement>())
  const before = useRef<Map<string, number> | null>(null)

  const register = (key: string, opts: { animate?: boolean } = {}) => (el: HTMLElement | null) => {
    const map = opts.animate === false ? measured.current : animated.current
    if (el) map.set(key, el)
    else map.delete(key)
  }
  const capture = () => {
    const tops = new Map<string, number>()
    for (const map of [animated.current, measured.current])
      map.forEach((el, key) => tops.set(key, el.getBoundingClientRect().top))
    before.current = tops
  }
  useLayoutEffect(() => {
    const prev = before.current
    if (!prev) return
    before.current = null
    animated.current.forEach((el, key) => {
      const from = prev.get(key) ?? (el.dataset.flipFrom ? prev.get(el.dataset.flipFrom) : undefined)
      if (from === undefined) return
      const dy = from - el.getBoundingClientRect().top
      if (Math.abs(dy) < 1) return
      el.style.transition = 'none'
      el.style.transform = `translateY(${dy}px)`
      requestAnimationFrame(() => {
        el.style.transition = 'transform 450ms cubic-bezier(0.2, 0.8, 0.2, 1)'
        el.style.transform = ''
      })
    })
  })
  return { register, capture }
}

export default function ProgramContractSelection({ detail, onDetail }: {
  detail: WorkflowStepDetail
  onDetail?: (d: WorkflowStepDetail) => void
}) {
  const ctx = detail.review_context || {}
  const report = ctx.report as ContractReport | null | undefined
  const interviewId = ctx.interview_id as string | null | undefined
  const serverSelection = ctx.selection as ContractSelectionPick[] | undefined
  const readOnly = detail.substage !== 'review_edit'

  const [pick, setPick] = useState<ContractSelectionPick | null>(serverSelection?.[0] ?? null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showMethod, setShowMethod] = useState(false)
  const flip = useFlip()
  // A new report can drop a pick it no longer offers — follow the server.
  useEffect(() => { setPick(serverSelection?.[0] ?? null) }, [report?.generated_at])

  // One scale for every speed bar: the longest range, capped so the calendar
  // ticks stay readable. A longer range runs to the end of its bar; its label
  // still states the full range.
  const speedScale = useMemo(
    () => Math.min(SPEED_SCALE_CAP, Math.max(1, ...(report?.types ?? []).map(
      (t) => t.duration?.max_months ?? t.duration?.typical_months ?? 0))),
    [report],
  )

  if (!report) {
    return <Card className="p-4 text-sm text-muted-foreground">No recommendation yet — run this step to generate one.</Card>
  }

  const choose = async (next: ContractSelectionPick) => {
    if (readOnly || saving) return
    const value = samePick(pick, next) ? null : next  // clicking the pick again clears it
    const prev = pick
    flip.capture()
    setPick(value); setSaving(true); setError(null)
    try {
      onDetail?.(await workflowsApi.saveSelection(detail.id, value ? [value] : []))
    } catch (e: any) {
      setError(e?.message || 'Could not save the selection')
      flip.capture()
      setPick(prev)
    } finally { setSaving(false) }
  }

  // The pick is pulled out to the top. A vehicle pick takes just that vehicle
  // with it; its type stays in the list with the rest of its vehicles (or
  // leaves the list, if it is vehicle-based and none are left).
  const pickedType = pick ? report.types.find((t) => t.contract_type_id === pick.contract_type_id) : undefined
  const pinned = pickedType && pick
    ? (pick.vehicle_id
      ? { ...pickedType, vehicles: pickedType.vehicles.filter((v) => v.vehicle_id === pick.vehicle_id) }
      : pickedType)
    : null
  const listed = report.types.flatMap((t) => {
    if (!pick || t.contract_type_id !== pick.contract_type_id) return [t]
    if (!pick.vehicle_id) return []
    const rest = t.vehicles.filter((v) => v.vehicle_id !== pick.vehicle_id)
    if (rest.length === 0 && t.is_vehicle_based) return []
    return [{ ...t, vehicles: rest }]
  })
  const pinnedKey = pick ? (pick.vehicle_id ? `pick:${pick.contract_type_id}:${pick.vehicle_id}` : `type:${pick.contract_type_id}`) : ''
  const cardProps = {
    ranked: report.is_ranked, speedScale, pick, onChoose: choose,
    disabled: readOnly || saving, register: flip.register,
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-sm text-destructive">{error}</div>}

      {!readOnly && (
        <Survey report={report} stepId={detail.id} interviewId={interviewId ?? null}
          onDetail={onDetail} onError={setError} />
      )}

      <div>
        <button type="button" onClick={() => setShowMethod(true)}
          className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2">
          How are rankings calculated?
        </button>
      </div>
      <RankingMethodModal open={showMethod} onClose={() => setShowMethod(false)} report={report} />
      {!report.is_ranked && (
        <div className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
          Not enough is known yet to rank these, so they're listed in no particular order.
          Answering the questions above is what ranks them.
        </div>
      )}

      <div className="space-y-3">
        {pinned && (
          <TypeRow key={pinnedKey} flipKey={pinnedKey} item={pinned} pinned
            flipFrom={pick?.vehicle_id ? `veh:${pick.contract_type_id}:${pick.vehicle_id}` : undefined}
            {...cardProps} />
        )}
        {listed.map((t) => (
          <TypeRow key={`type:${t.contract_type_id}`} flipKey={`type:${t.contract_type_id}`} item={t} {...cardProps} />
        ))}
        {report.types.length === 0 && (
          <Card className="p-4 text-sm text-muted-foreground">No contract type is currently available for this program.</Card>
        )}
      </div>

      {report.notes.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
          {report.notes.map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}

      {report.ruled_out.length > 0 && (
        <details className="group rounded-md border border-border">
          <summary className="cursor-pointer select-none px-3 py-2 text-sm text-foreground flex items-center gap-2">
            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
            Ruled out ({report.ruled_out.length})
          </summary>
          <ul className="px-4 pb-3 space-y-2">
            {report.ruled_out.map((r) => (
              <li key={r.contract_type_id} className="text-sm">
                <div className="font-medium text-foreground">{r.name}</div>
                {r.failed.map((f) => (
                  <div key={f.spec_key} className="text-xs text-muted-foreground">
                    Not met: {f.condition} <span className="italic">({f.sources.join('; ')})</span>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

// ── the survey ──────────────────────────────────────────────────────────────

function Survey({ report, stepId, interviewId, onDetail, onError }: {
  report: ContractReport; stepId: number; interviewId: string | null
  onDetail?: (d: WorkflowStepDetail) => void
  onError: (msg: string | null) => void
}) {
  // Skips live for the visit; an answer is on record and leaves the survey by
  // itself (the recomputed report no longer asks it).
  const [skipped, setSkipped] = useState<string[]>([])
  const [answeredCount, setAnsweredCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [showFacts, setShowFacts] = useState(false)

  const remaining = (report.survey ?? []).filter((q) => !skipped.includes(q.spec_id))
  const q = remaining[0]
  const facts = report.known_facts ?? []

  // Nothing left to ask: the box goes away. Skipped questions leave a quiet
  // way back rather than the whole card.
  if (!q) {
    if (skipped.length === 0) return null
    return (
      <div className="text-xs text-muted-foreground">
        {skipped.length} skipped question{skipped.length === 1 ? '' : 's'} could still sharpen the ranking.{' '}
        <button type="button" className="underline underline-offset-2 hover:text-foreground"
          onClick={() => setSkipped([])}>Revisit</button>
      </div>
    )
  }

  const answer = async (value: string) => {
    if (!q || !interviewId) return
    setBusy(true); onError(null)
    try {
      await api.post(`/api/documents/interviews/${interviewId}/record-answer/`, { question: q.spec_id, value })
      const d = await workflowsApi.refreshStep(stepId)
      setAnsweredCount((n) => n + 1)
      onDetail?.(d)
    } catch (e: any) {
      onError(e?.message || 'Could not record the answer')
    } finally { setBusy(false) }
  }

  return (
    <Card className="p-4 space-y-3">
      <div>
        <div className="text-sm font-medium text-foreground">Sharpen eligibility and ranking</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          The list below is the best we can do with what we know. To determine eligibility for these contract
          types, answer the questions here — each answer re-ranks the list and drops questions that no longer matter.{' '}
          {facts.length > 0 ? (
            <>
              We've already applied{' '}
              <button type="button" className="underline underline-offset-2 hover:text-foreground"
                onClick={() => setShowFacts((s) => !s)}>
                {facts.length} fact{facts.length === 1 ? '' : 's'} on record
              </button>{' '}about this program.
            </>
          ) : (
            <>Nothing about this program's contracting is on record yet.</>
          )}
        </p>
        {showFacts && facts.length > 0 && (
          <ul className="mt-2 text-xs text-muted-foreground space-y-0.5 pl-4 list-disc">
            {facts.map((f) => (
              <li key={f.spec_key}><span className="text-foreground">{f.spec_name}:</span> {f.value_label}</li>
            ))}
          </ul>
        )}
      </div>

        <div key={q.spec_id} className="animate-fade-in rounded-md border border-border p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {q.kind === 'legal' ? 'Eligibility' : 'Fit'}
            </span>
            <span className="text-xs text-muted-foreground">
              {answeredCount > 0 && `${answeredCount} answered · `}{remaining.length} to go
            </span>
          </div>
          <div className="text-sm text-foreground">{q.question}</div>
          <div className="text-xs text-muted-foreground">
            Affects {q.affects.map((a) => a.name).join(', ')}
            {q.rule_group ? ' · any one of this group is enough' : ''}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <AnswerControl q={q} onAnswer={answer} disabled={busy || !interviewId} />
            <button type="button" disabled={busy}
              onClick={() => setSkipped((s) => [...s, q.spec_id])}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-40 ml-1">
              Skip
            </button>
            {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          </div>
          {!interviewId && (
            <div className="text-xs text-muted-foreground">Re-run this step to enable answering.</div>
          )}
        </div>
    </Card>
  )
}

function AnswerControl({ q, onAnswer, disabled }: {
  q: ContractSurveyQuestion; onAnswer: (v: string) => void; disabled: boolean
}) {
  const [draft, setDraft] = useState('')
  const choices = q.spec_type === 'boolean'
    ? [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]
    : q.options
  if (choices.length > 0) {
    return (
      <>
        {choices.map((o) => (
          <Button key={o.value} size="sm" variant="outline" disabled={disabled} onClick={() => onAnswer(o.value)}>
            {o.label}
          </Button>
        ))}
      </>
    )
  }
  return (
    <div className="flex gap-2 max-w-sm">
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} disabled={disabled}
        type={q.spec_type === 'numeric' ? 'number' : 'text'} />
      <Button size="sm" variant="outline" disabled={disabled || !draft.trim()} onClick={() => onAnswer(draft.trim())}>
        Save
      </Button>
    </div>
  )
}

// ── the list ────────────────────────────────────────────────────────────────

const SPEED_SCALE_CAP = 24
const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' })

/** Calendar ticks across the bar's scale: "Today", then each January and July. */
function calendarTicks(scaleMonths: number): { label: string; months: number }[] {
  const now = new Date()
  const ticks = [{ label: 'Today', months: 0 }]
  const d = new Date(now.getFullYear(), now.getMonth() < 6 ? 6 : 12, 1)
  for (; ; d.setMonth(d.getMonth() + 6)) {
    const months = (d.getFullYear() - now.getFullYear()) * 12 + d.getMonth() - now.getMonth() - (now.getDate() - 1) / 30
    // Labels start at their tick, so the last one needs room before the edge.
    if (months > scaleMonths - 4) break
    ticks.push({ label: MONTH.format(d), months })
  }
  return ticks
}

function SpeedBar({ duration, scale }: { duration: ContractTypeItem['duration']; scale: number }) {
  const ticks = useMemo(() => calendarTicks(scale), [scale])
  if (!duration) return null
  const lo = duration.min_months ?? duration.typical_months
  const hi = duration.max_months ?? duration.typical_months
  const pct = (m: number) => `${(100 * m) / scale}%`
  return (
    <div className="flex items-start gap-2 shrink-0" title={duration.note}>
      <div className="w-64 pt-1.5">
        <div className="relative h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="absolute inset-y-0 rounded-full bg-primary"
            style={{ left: pct(lo), width: `max(4px, ${pct(hi - lo)})` }} />
        </div>
        <div className="relative h-4 mt-1">
          {ticks.map((t) => (
            <span key={t.label}
              className="absolute top-0 text-[10px] leading-none text-muted-foreground whitespace-nowrap"
              style={{ left: pct(t.months) }}>
              <span className="block w-px h-1 bg-border mb-0.5" />
              {t.label}
            </span>
          ))}
        </div>
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-24 whitespace-nowrap">
        {lo === hi ? `${lo}` : `${lo}–${hi}`} months
      </span>
    </div>
  )
}

function ConditionList({ conditions }: { conditions: ContractCondition[] }) {
  return (
    <ul className="space-y-1 pl-4 list-disc text-xs text-muted-foreground">
      {conditions.map((c, i) => (
        <li key={i}>
          {c.condition}
          {c.any_of && (
            <ul className="mt-1 space-y-0.5 pl-4 list-[circle]">
              {c.any_of.map((alt) => <li key={alt}>{alt}</li>)}
            </ul>
          )}
        </li>
      ))}
    </ul>
  )
}

const CONDITIONS_SHOWN = 3

/** A radio mark: the one-pick control for types and vehicles. */
function PickMark({ on }: { on: boolean }) {
  return (
    <span aria-hidden
      className="flex h-4 w-4 items-center justify-center rounded-full border border-input bg-background"
      style={on ? { borderColor: 'var(--success)' } : undefined}>
      {on && <span className="h-2 w-2 rounded-full" style={{ background: 'var(--success)' }} />}
    </span>
  )
}

// Clicks inside these don't pick the card.
const stop = (e: React.SyntheticEvent) => e.stopPropagation()

type RowProps = {
  item: ContractTypeItem
  ranked: boolean
  speedScale: number
  pick: ContractSelectionPick | null
  onChoose: (p: ContractSelectionPick) => void
  disabled: boolean
  register: (key: string, opts?: { animate?: boolean }) => (el: HTMLElement | null) => void
  flipKey: string
  flipFrom?: string
  pinned?: boolean
}

/** One contract type: the big rank number on the left, the card beside it. */
function TypeRow({ item, ranked, speedScale, pick, onChoose, disabled, register, flipKey, flipFrom, pinned }: RowProps) {
  const [allConditions, setAllConditions] = useState(false)
  const typePick = { contract_type_id: item.contract_type_id, vehicle_id: null }
  const typeSelectable = !item.is_vehicle_based
  const typePicked = samePick(pick, typePick)
  const conds = item.unchecked_conditions ?? []
  const shown = allConditions ? conds : conds.slice(0, CONDITIONS_SHOWN)
  const label = pinned ? 'Your selection' : `Select ${item.name}`
  return (
    <div ref={register(flipKey)} data-flip-from={flipFrom} className="flex items-center gap-4">
      {ranked && (
        // The pick keeps the column (so cards stay aligned) but not its number:
        // it is no longer "#4 of the list", it is the selection.
        <div className="w-10 shrink-0 text-right text-3xl font-bold tabular-nums text-muted-foreground">
          {pinned ? null : item.rank}
        </div>
      )}
      <Card
        role={typeSelectable ? 'button' : undefined}
        aria-label={typeSelectable ? label : undefined}
        aria-pressed={typeSelectable ? typePicked : undefined}
        onClick={typeSelectable && !disabled ? () => onChoose(typePick) : undefined}
        className={`relative flex-1 min-w-0 p-4 gap-0 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:scale-[1.005] hover:shadow-md ${typeSelectable && !disabled ? 'cursor-pointer' : ''}`}
        style={pinned ? { outline: '2px solid var(--success)', outlineOffset: '-1px' } : undefined}>
        {pinned && (
          <span className="absolute -top-2.5 right-4 rounded-full bg-card px-2 text-xs font-medium leading-5"
            style={{ color: 'var(--success)', boxShadow: '0 0 0 1.5px var(--success)' }}>
            Selected
          </span>
        )}
        <div className="flex items-start gap-3">
          {/* Same height as the title line, so the mark centres on the header. */}
          <div className="h-6 flex items-center shrink-0">
            {typeSelectable ? <PickMark on={typePicked} /> : <span className="w-4" />}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 min-h-6">
                <span className="font-medium text-foreground">{item.name}</span>
                {item.authority && <span className="text-xs text-muted-foreground">{item.authority}</span>}
                {item.decision === 'eligible' && <Pill style={tint('success')}>Eligible</Pill>}
                {item.confidence.level !== 'low' && (
                  <Pill title={item.confidence.reason}>
                    {item.confidence.level === 'high' ? 'High' : 'Medium'} confidence
                  </Pill>
                )}
              </div>
              <SpeedBar duration={item.duration} scale={speedScale} />
            </div>

            {item.is_vehicle_based && !pinned && item.vehicles.length > 0 && (
              <div className="text-xs text-muted-foreground">Used by ordering off a vehicle — pick one below.</div>
            )}

            {item.preferences_matched.length > 0 && (
              <div className="text-xs text-foreground">
                Fits:{' '}
                {item.preferences_matched.map((p, i) => (
                  <span key={p.spec_key} title={`${p.rationale} (${p.sources.join('; ')})`}>
                    {i > 0 && ', '}{p.spec_name.toLowerCase()} — {p.value_label}
                  </span>
                ))}
              </div>
            )}

            {conds.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs text-foreground">
                  To use this contract type, we'll need to satisfy {conds.length === 1 ? 'this condition' : 'these conditions'} we
                  weren't able to check:
                </div>
                <ConditionList conditions={shown} />
                {conds.length > CONDITIONS_SHOWN && (
                  <button type="button" onClick={(e) => { stop(e); setAllConditions((s) => !s) }}
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline pl-4">
                    {allConditions ? 'Show fewer' : `+${conds.length - CONDITIONS_SHOWN} more`}
                  </button>
                )}
              </div>
            )}

            {item.gates_passed.length > 0 && (
              <details className="text-xs" onClick={stop}>
                <summary className="cursor-pointer text-muted-foreground">
                  Conditions met ({item.gates_passed.length})
                </summary>
                <ul className="mt-1 space-y-1 pl-4 list-disc">
                  {item.gates_passed.map((g) => (
                    <li key={g.spec_key} className="text-muted-foreground">
                      {g.condition} <span className="italic">({g.sources.join('; ')})</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}

            {item.vehicles.length > 0 && (
              <div className="border-l border-border pl-4 space-y-1">
                {item.vehicles.map((v) => (
                  <VehicleRow key={v.vehicle_id} typeId={item.contract_type_id} v={v} pick={pick}
                    onChoose={onChoose} disabled={disabled} register={register} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}

function VehicleRow({ typeId, v, pick, onChoose, disabled, register }: {
  typeId: string; v: ContractVehicleItem
  pick: ContractSelectionPick | null
  onChoose: (p: ContractSelectionPick) => void
  disabled: boolean
  register: RowProps['register']
}) {
  const vehiclePick = { contract_type_id: typeId, vehicle_id: v.vehicle_id }
  const on = samePick(pick, vehiclePick)
  return (
    <div ref={register(`veh:${typeId}:${v.vehicle_id}`, { animate: false })}
      role="button" aria-label={`Select ${v.name}`} aria-pressed={on}
      onClick={(e) => { stop(e); if (!disabled) onChoose(vehiclePick) }}
      className={`flex items-start gap-3 rounded-md -mx-2 px-2 py-1.5 transition-colors hover:bg-accent ${disabled ? '' : 'cursor-pointer'}`}>
      <div className="h-5 flex items-center shrink-0"><PickMark on={on} /></div>
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2 min-h-5">
          <span className="text-sm font-medium text-foreground">{v.name}</span>
          {v.url && (
            <a href={v.url} target="_blank" rel="noreferrer" onClick={stop}
              className="text-muted-foreground hover:text-foreground">
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          Matches {v.matched_families.map((f) => f.family_name).join(', ')}
          {v.last_verified ? ` · last verified ${v.last_verified}` : ''}
        </div>
        {(v.unchecked_conditions ?? []).length > 0 && (
          <details className="text-xs" onClick={stop}>
            <summary className="cursor-pointer text-muted-foreground">
              To order off this vehicle, confirm {v.unchecked_conditions.length} conditions we couldn't check
            </summary>
            <div className="mt-1"><ConditionList conditions={v.unchecked_conditions} /></div>
          </details>
        )}
      </div>
    </div>
  )
}

// ── "How are rankings calculated?" ──────────────────────────────────────────

function RankingMethodModal({ open, onClose, report }: {
  open: boolean; onClose: () => void; report: ContractReport
}) {
  const families = report.families.map((f) => f.family_name)
  return (
    <Modal isOpen={open} onClose={onClose} title="How are rankings calculated?" size="md">
      <div className="px-6 py-5 space-y-4 text-sm text-foreground">
        <p className="text-muted-foreground">
          Contract types are ordered by what we know about this program, strongest evidence first. Each step
          only breaks ties left by the one before it.
        </p>
        <ol className="space-y-3 list-decimal pl-5">
          <li>
            <span className="font-medium">Legal eligibility.</span> Types whose legal conditions are all confirmed
            rank above types with conditions still open. A condition answered "no" moves a type to Ruled out.
          </li>
          <li>
            <span className="font-medium">Fit.</span> Your answers about the buy (what you're buying, whether a
            commercial solution exists, data rights, competition) lift the types that suit them. A poor fit never
            rules a type out.
          </li>
          <li>
            <span className="font-medium">Matching vehicles.</span> Existing contracts you can order from are matched
            to your accepted solution families
            {families.length > 0 ? <> ({families.join(', ')})</> : null} by technology. Types with a matching vehicle
            rank higher, and IDIQ and Schedule orders only appear when one matches.
          </li>
          <li>
            <span className="font-medium">Speed.</span> Faster estimated time to award wins a tie. These are rough
            Overmatch estimates, not sourced figures.
          </li>
        </ol>
        <p className="text-muted-foreground">
          Legal conditions come from the governing statutes and regulations; fit follows Overmatch's
          mechanism-matching logic. We don't yet have past-order data to confirm a fit with precedent, and
          vehicle ceilings, CMMC levels and clearances aren't public, so those stay listed as conditions to confirm.
        </p>
      </div>
    </Modal>
  )
}
